import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  BlockStack,
  TextField,
  Select,
  Checkbox,
  Toast,
  Frame,
  SkeletonBodyText,
} from '@shopify/polaris';
import { useSettings } from '../hooks/useSettings';
import { useLanguage } from '../providers/LanguageProvider';
import { useShopify } from '../providers/AppBridgeProvider';
import SizeGuidesSection from '../components/SizeGuidesSection';
import TargetingSection from '../components/TargetingSection';
import type { FitOverride, FitPreference, PageName, Targeting } from '../types';

interface SettingsPageProps {
  onNavigate: (page: PageName) => void;
}

/** What the backend hands back for a merchant who never set a rule. */
const DEFAULT_TARGETING: Targeting = {
  tryon: { mode: 'all', categories: [], productIds: [] },
  size: { mode: 'all', categories: [], productIds: [] },
};

const COOLDOWN_PRESETS = [
  { value: '30', labelKey: 'cooldown30min' },
  { value: '60', labelKey: 'cooldown1hr' },
  { value: '120', labelKey: 'cooldown2hr' },
  { value: '180', labelKey: 'cooldown3hr' },
  { value: '360', labelKey: 'cooldown6hr' },
  { value: '720', labelKey: 'cooldown12hr' },
  { value: '1440', labelKey: 'cooldown24hr' },
  { value: 'custom', labelKey: 'cooldownCustomLabel' },
] as const;

export default function SettingsPage({ onNavigate: _onNavigate }: SettingsPageProps) {
  const {
    settings,
    loading,
    saving,
    error,
    updateSettings,
    createSizeGuide,
    updateSizeGuide,
    deleteSizeGuide,
    fetchProductTypes,
    sizeGuideBusy,
    sizeGuideError,
  } = useSettings();
  const { t } = useLanguage();
  const { shopify } = useShopify();
  const s = t.settings;

  const [enabled, setEnabled] = useState(false);
  const [maxGenerations, setMaxGenerations] = useState('5');
  const [cooldownSelect, setCooldownSelect] = useState('180');
  const [customCooldown, setCustomCooldown] = useState('180');
  const [targeting, setTargeting] = useState<Targeting>(DEFAULT_TARGETING);
  const [defaultFitPreference, setDefaultFitPreference] = useState<FitPreference>('regular');
  const [fitOverrides, setFitOverrides] = useState<FitOverride[]>([]);
  const [fitPickerError, setFitPickerError] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [productTypeSuggestions, setProductTypeSuggestions] = useState<string[]>([]);

  // Advisory suggestions for the product-type combobox. Depends on
  // fetchProductTypes rather than an empty array for the same reason the
  // settings fetch below does: authenticatedFetch (and so this callback)
  // only gets its real identity once App Bridge resolves the shop, a beat
  // after mount, so the effect must re-fire when that identity changes or
  // the request goes out unauthenticated. An empty list is still a normal
  // answer either way (the endpoint degrades to empty on any backend
  // trouble) and leaves the field usable as free text regardless.
  useEffect(() => {
    let cancelled = false;
    fetchProductTypes().then((types) => {
      if (!cancelled) setProductTypeSuggestions(types);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchProductTypes]);

  // Sync form state with fetched settings.
  //
  // Keyed on the VALUES this form owns, not on the settings object identity:
  // uploading a size guide replaces that object too, and re-hydrating on it
  // would throw away targeting the merchant had edited but not yet saved.
  const hydratedFrom = useRef<string | null>(null);
  useEffect(() => {
    if (!settings) return;
    const snapshot = JSON.stringify({
      rateLimiting: settings.rateLimiting,
      targeting: settings.targeting,
      defaultFitPreference: settings.defaultFitPreference,
      fitOverrides: settings.fitOverrides,
    });
    if (hydratedFrom.current === snapshot) return;
    hydratedFrom.current = snapshot;

    const rl = settings.rateLimiting;
    setEnabled(rl.enabled);
    setMaxGenerations(String(rl.maxGenerationsPerVisitor));
    const presetMatch = COOLDOWN_PRESETS.find(
      (p) => p.value !== 'custom' && p.value === String(rl.cooldownMinutes)
    );
    if (presetMatch) {
      setCooldownSelect(presetMatch.value);
    } else {
      setCooldownSelect('custom');
      setCustomCooldown(String(rl.cooldownMinutes));
    }
    setTargeting(settings.targeting ?? DEFAULT_TARGETING);
    setDefaultFitPreference(settings.defaultFitPreference ?? 'regular');
    setFitOverrides(settings.fitOverrides ?? []);
  }, [settings]);

  const getCooldownMinutes = useCallback((): number => {
    if (cooldownSelect === 'custom') {
      return parseInt(customCooldown, 10) || 180;
    }
    return parseInt(cooldownSelect, 10);
  }, [cooldownSelect, customCooldown]);

  const handleSave = useCallback(async () => {
    // Nothing loaded means the form is showing its own defaults, not the
    // merchant's settings. Saving them would quietly reset every targeting
    // rule to allow-all and put the try-on back on the whole catalogue.
    if (!settings) return;

    const maxGen = parseInt(maxGenerations, 10);
    const cooldown = getCooldownMinutes();

    if (!maxGen || maxGen < 1 || maxGen > 100) return;
    if (!cooldown || cooldown < 1 || cooldown > 10080) return;

    const success = await updateSettings({
      rateLimiting: {
        enabled,
        maxGenerationsPerVisitor: maxGen,
        cooldownMinutes: cooldown,
      },
      targeting,
      defaultFitPreference,
      fitOverrides,
    });

    setToastError(!success);
    setToastActive(true);
  }, [
    settings,
    enabled,
    maxGenerations,
    getCooldownMinutes,
    targeting,
    defaultFitPreference,
    fitOverrides,
    updateSettings,
  ]);

  const addFitOverride = useCallback(async () => {
    if (!shopify?.resourcePicker || fitOverrides.length >= 250) return;
    setFitPickerError(false);
    try {
      const selection = await shopify.resourcePicker({ type: 'collection', action: 'add', multiple: 250 - fitOverrides.length });
      if (!selection) return;
      for (const item of selection) {
        if (!/^gid:\/\/shopify\/Collection\/[1-9][0-9]*$/.test(item.id) || !item.title?.trim()) {
          setFitPickerError(true);
          return;
        }
      }
      setFitOverrides((current) => {
        const next = [...current];
        const seen = new Set(current.map((entry) => entry.categoryId));
        for (const item of selection) {
          if (next.length >= 250) break;
          if (seen.has(item.id)) continue;
          const label = item.title?.trim();
          if (!label) continue;
          seen.add(item.id);
          next.push({
            categoryId: item.id,
            categoryLabel: label,
            preference: defaultFitPreference,
            priority: next.length * 10,
          });
        }
        return next;
      });
    } catch {
      setFitPickerError(true);
    }
  }, [shopify, fitOverrides, defaultFitPreference]);

  const cooldownOptions = COOLDOWN_PRESETS.map((p) => ({
    label: s[p.labelKey as keyof typeof s] as string,
    value: p.value,
  }));

  const fitPreferenceOptions = [
    { label: s.fitPreferenceTight, value: 'tight' },
    { label: s.fitPreferenceRegular, value: 'regular' },
    { label: s.fitPreferenceLoose, value: 'loose' },
  ];

  const formatCooldownDisplay = (): string => {
    const minutes = getCooldownMinutes();
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMin = minutes % 60;
    if (remainingMin === 0) return `${hours}h`;
    return `${hours}h ${remainingMin}min`;
  };

  if (loading) {
    return (
      <Page title={s.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={6} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title={s.title}>
      <Layout>
        <Layout.Section>
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {s.rateLimitingTitle}
              </Text>

              <Banner tone="info">
                <p>{s.rateLimitingDescription}</p>
              </Banner>

              <Checkbox
                label={s.enableRateLimiting}
                checked={enabled}
                onChange={setEnabled}
              />

              {enabled && (
                <BlockStack gap="300">
                  <TextField
                    label={s.maxGenerations}
                    type="number"
                    value={maxGenerations}
                    onChange={setMaxGenerations}
                    min={1}
                    max={100}
                    helpText={s.maxGenerationsHelp}
                    autoComplete="off"
                  />

                  <Select
                    label={s.cooldownPeriod}
                    options={cooldownOptions}
                    value={cooldownSelect}
                    onChange={setCooldownSelect}
                  />

                  {cooldownSelect === 'custom' && (
                    <TextField
                      label={s.cooldownCustom}
                      type="number"
                      value={customCooldown}
                      onChange={setCustomCooldown}
                      min={1}
                      max={10080}
                      suffix={s.cooldownMinutes}
                      autoComplete="off"
                    />
                  )}

                  <Text as="p" tone="subdued">
                    {s.exampleText(
                      parseInt(maxGenerations, 10) || 5,
                      formatCooldownDisplay()
                    )}
                  </Text>
                </BlockStack>
              )}

              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!settings}
              >
                {s.save}
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text variant="headingMd" as="h2">
                {s.fitPreferenceTitle}
              </Text>
              <Text as="p" tone="subdued">
                {s.fitPreferenceDescription}
              </Text>
              <Select
                label={s.fitPreferenceLabel}
                options={fitPreferenceOptions}
                value={defaultFitPreference}
                onChange={(value) => setDefaultFitPreference(value as FitPreference)}
                helpText={s.fitPreferenceHelp}
              />
              <Text variant="headingSm" as="h3">{s.fitOverridesTitle}</Text>
              <Text as="p" tone="subdued">{s.fitOverridesHelp}</Text>
              {fitOverrides.length === 0 && <Text as="p" tone="subdued">{s.fitOverridesEmpty}</Text>}
              {fitOverrides.map((override) => (
                <div key={override.categoryId}>
                  <Text as="p" fontWeight="medium">{override.categoryLabel}</Text>
                  <Select
                    label={s.fitOverridesPreference(override.categoryLabel)}
                    options={fitPreferenceOptions}
                    value={override.preference}
                    onChange={(value) => setFitOverrides((current) => current.map((entry) => entry.categoryId === override.categoryId ? { ...entry, preference: value as FitPreference } : entry))}
                  />
                  <TextField
                    label={s.fitOverridesPriority(override.categoryLabel)}
                    type="number"
                    min={0}
                    max={9999}
                    value={String(override.priority)}
                    onChange={(value) => setFitOverrides((current) => current.map((entry) => entry.categoryId === override.categoryId ? { ...entry, priority: Number(value) } : entry))}
                    autoComplete="off"
                  />
                  <Button onClick={() => setFitOverrides((current) => current.filter((entry) => entry.categoryId !== override.categoryId))}>
                    {s.fitOverridesRemove(override.categoryLabel)}
                  </Button>
                </div>
              ))}
              <Button onClick={addFitOverride} disabled={!shopify?.resourcePicker || fitOverrides.length >= 250}>
                {s.fitOverridesAdd}
              </Button>
              {!shopify?.resourcePicker && <Text as="p" tone="subdued">{s.fitOverridesPickerUnavailable}</Text>}
              {fitPickerError && <Banner tone="critical">{s.targetingPickerFailed}</Banner>}
              <Button
                variant="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!settings}
              >
                {s.save}
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* The parsed table goes back in front of the merchant rather than
            being saved silently: a garment guide read as a body guide moves
            every recommendation, and they are the only one who can see it. */}
        <Layout.Section>
          <SizeGuidesSection
            guides={settings?.sizeGuides ?? []}
            coverageReport={settings?.sizeCoverageReport ?? null}
            // Passed straight through rather than wrapped: an intermediate
            // arrow that forwards an omitted `name` as an explicit
            // `undefined` argument is not the same call as one that omits it,
            // and the hook's own optional param already handles either.
            onCreate={createSizeGuide}
            onPatch={updateSizeGuide}
            onDelete={deleteSizeGuide}
            busy={sizeGuideBusy}
            error={sizeGuideError}
            // Same reason the Save button is disabled: with nothing loaded a
            // mutation would reach the server but the result could not be
            // shown back, and confirming it is what this section is for.
            disabled={!settings}
            productTypeSuggestions={productTypeSuggestions}
          />
        </Layout.Section>

        <Layout.Section>
          <BlockStack gap="400">
            <TargetingSection
              targeting={targeting}
              onChange={setTargeting}
              syncedAt={settings?.targetingSyncedAt ?? null}
              syncError={settings?.targetingSyncError ?? null}
              disabled={saving}
            />
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!settings}>
              {s.save}
            </Button>
          </BlockStack>
        </Layout.Section>
      </Layout>

      {toastActive && (
        <Frame>
          <Toast
            content={toastError ? s.saveError : s.saveSuccess}
            error={toastError}
            onDismiss={() => setToastActive(false)}
          />
        </Frame>
      )}
    </Page>
  );
}
